import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { getDashboardStats, getAllBookings } from '../../utils/adminApi';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [weekBookings, setWeekBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, bookingsData] = await Promise.all([
          getDashboardStats(),
          getAllBookings()
        ]);
        setStats(statsData);
        setRecentBookings(bookingsData.slice(0, 5));
        
        // Get this week's bookings
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
        
        const weeklyBookings = bookingsData.filter(b => {
          const bookingDate = new Date(b.date);
          return bookingDate >= startOfWeek && bookingDate <= endOfWeek;
        });
        setWeekBookings(weeklyBookings);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getDayBookings = (dayOffset) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    const targetDate = new Date(startOfWeek);
    targetDate.setDate(startOfWeek.getDate() + dayOffset);
    const dateStr = targetDate.toISOString().split('T')[0];
    
    return weekBookings.filter(b => b.date === dateStr);
  };

  if (loading) {
    return <div className="text-zinc-400">Loading...</div>;
  }

  const statCards = [
    { title: 'Total Bookings', value: stats?.totalBookings || 0, icon: Calendar, color: 'blue' },
    { title: 'Pending', value: stats?.pendingBookings || 0, icon: Clock, color: 'yellow' },
    { title: 'Confirmed', value: stats?.confirmedBookings || 0, icon: Calendar, color: 'green' },
    { title: 'Today', value: stats?.todayBookings || 0, icon: Calendar, color: 'purple' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-zinc-100">Dashboard</h1>
        <p className="text-zinc-400 mt-1">Overview of your detailing business</p>
      </div>

      {/* Week Calendar - Moved to top */}
      <Card className="bg-zinc-900 border-zinc-800 mb-8">
        <CardHeader>
          <CardTitle className="text-zinc-100">This Week's Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
              const dayBookings = getDayBookings(index);
              const today = new Date();
              const startOfWeek = new Date(today);
              startOfWeek.setDate(today.getDate() - today.getDay());
              const targetDate = new Date(startOfWeek);
              targetDate.setDate(startOfWeek.getDate() + index);
              const isToday = targetDate.toDateString() === today.toDateString();
              
              return (
                <div key={day} className={`text-center p-2 rounded-lg border ${
                  isToday ? 'border-zinc-100 bg-zinc-800' : 'border-zinc-800 bg-zinc-950'
                }`}>
                  <div className="text-xs text-zinc-500 mb-1">{day}</div>
                  <div className="text-lg font-bold text-zinc-100">{targetDate.getDate()}</div>
                  <div className="text-xs mt-1">
                    {dayBookings.length > 0 ? (
                      <span className="text-green-400">{dayBookings.length} booking{dayBookings.length > 1 ? 's' : ''}</span>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.title} className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">{stat.title}</CardTitle>
              <stat.icon className="w-4 h-4 text-zinc-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-zinc-100">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Bookings */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-zinc-100">Recent Bookings</CardTitle>
            <Link to="/admin/bookings" className="text-sm text-zinc-400 hover:text-zinc-100">
              View all →
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentBookings.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">No bookings yet</p>
          ) : (
            <div className="space-y-4">
              {recentBookings.map((booking) => (
                <div key={booking.bookingId} className="flex items-center justify-between p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <div className="flex-1">
                    <div className="font-semibold text-zinc-100">{booking.customerName}</div>
                    <div className="text-sm text-zinc-400">{booking.serviceName}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {new Date(booking.date).toLocaleDateString()} at {booking.time}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-zinc-100">€{booking.totalAmount}</div>
                    <div className={`text-xs mt-1 px-2 py-1 rounded inline-block ${
                      booking.status === 'confirmed' ? 'bg-green-500/10 text-green-500' :
                      booking.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' :
                      booking.status === 'completed' ? 'bg-blue-500/10 text-blue-500' :
                      'bg-red-500/10 text-red-500'
                    }`}>
                      {booking.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
